"use client";
import { useSession } from "next-auth/react";
import { useState } from "react";
import { trackEvent } from "~/connectors/analytics.connector";
import { AnalyticsEvents } from "~/connectors/analyticsEvents";
import { anonymousVouchersService } from "~/connectors/vouchers.connector";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";

interface VoucherLaunchOfferDialogProps {
  open: boolean;
  onClose: () => void;
}

export default function VoucherLaunchOfferDialog({
  open,
  onClose,
}: VoucherLaunchOfferDialogProps) {
  const { data: session } = useSession();
  const [email, setEmail] = useState(session?.user?.email || "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const isUserLoggedIn =
    !!session?.user?.email && session.user.email.trim() !== "";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    trackEvent(AnalyticsEvents.VOUCHER_REQUEST_BUTTON_CLICKED);

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await anonymousVouchersService.requestVoucher({
        email: email.trim(),
      });
      setSuccess(response.success);
    } catch (err) {
      setError(err instanceof Error ? err.message : "A apărut o eroare");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      trackEvent(AnalyticsEvents.VOUCHER_REQUEST_POPUP_CLOSED);
      onClose();
      setError(null);
      setSuccess(false);
    }
  };

  const renderForm = () => (
    <>
      <DialogTitle sx={{ textAlign: "center", pb: 1 }}>
        <Typography variant="h5" component="div" sx={{ fontWeight: "bold" }}>
          🎁 Primește -70% la primul cântecel
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <Typography
            component="div"
            sx={{
              fontSize: "3.75rem",
              fontWeight: "bold",
              color: "primary.main",
              my: 3,
              lineHeight: 1,
            }}
          >
            -70%
          </Typography>
          <Typography variant="body1">
            Lasă-ne adresa de email și îți trimitem instant codul de reducere
            pentru melodia personalizată a copilului tău.
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label={
              isUserLoggedIn
                ? "Email-ul tău din cont"
                : "📧 Introdu emailul tău"
            }
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isSubmitting || isUserLoggedIn}
            sx={{ mb: 2 }}
            required
          />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            loading={isSubmitting}
            disabled={!email.trim()}
            sx={{ py: 1.5 }}
          >
            Trimite-mi codul
          </Button>
        </Box>
      </DialogContent>
    </>
  );

  const renderSuccess = () => (
    <DialogContent>
      <Alert severity="success" sx={{ mb: 2 }}>
        Codul a fost trimis pe email-ul tău!
      </Alert>

      <Typography variant="body2" sx={{ textAlign: "center" }}>
        Verifică-ți inbox-ul (sau folderul de spam) pentru email-ul nostru cu
        codul tău de reducere. Dacă nu îl găsești, încearcă să trimiți din nou
        sau contactează-ne pentru asistență.
      </Typography>
      <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
        <Button
          variant="contained"
          onClick={handleClose}
          sx={{ py: 1.5, px: 4 }}
        >
          Închide
        </Button>
      </Box>
    </DialogContent>
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      {success ? renderSuccess() : renderForm()}
    </Dialog>
  );
}
